// @ts-strict-ignore
import { Accordion, Background, Content, Footer, Header } from '@components/FullscreenFilterModal'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { FilterAction, FilterState, Filters } from '@components/_product-overview/reducer'
import { Checkbox, Link } from '@gassan-ui'
import { ArticleAttributeFilter, BrandCollectionFilter, BrandTypeFilter } from '@generated'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { getFilteredAttributes } from '../helpers/get-filtered-attributes'

type Props = Omit<FilterState, 'sort'> & {
  showRolexLink?: boolean
  onRequestClose?: () => void
  dispatch: React.Dispatch<FilterAction>
  attributes: ArticleAttributeFilter[]
  brandOptions: BrandTypeFilter[]
  collectionOptions: BrandCollectionFilter[]
  fixedFilters: Filters
  totalAppliedFitlers: number
  isPreOwned: boolean
}

const FilterOverlay = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      showRolexLink = false,
      onRequestClose,
      dispatch,
      filters,
      fixedFilters,
      attributes,
      brandOptions,
      collectionOptions,
      brands,
      collections,
      isPreOwned,
      totalAppliedFitlers,
    },
    ref,
  ) => {
    const { t } = useTranslation(['filters', 'other'])
    const [state, setState] = useState(filters)
    const [selectedBrands, setSelectedBrands] = useState(brands)
    const [selectedCollections, setSelectedCollections] = useState(collections)

    const priceCategory = attributes.length
      ? attributes.filter((item) => item.attributeName === 'priceCategory')[0]
      : null

    const toggle = (key: number, value: number) => {
      if (state[key]) {
        setState((state) => ({
          ...state,
          [key]: state[key].includes(value)
            ? state[key].filter((i) => i !== value)
            : [...state[key], value],
        }))
      } else {
        setState((state) => ({
          ...state,
          [key]: [value],
        }))
      }
    }

    const toggleBrand = (key: number) =>
      setSelectedBrands((brands) =>
        brands.includes(key) ? brands.filter((b) => b !== key) : [key, ...brands],
      )
    const toggleCollection = (key: number) =>
      setSelectedCollections((collection) =>
        collection.includes(key) ? collection.filter((b) => b !== key) : [key, ...collection],
      )

    let totalSelectedLocal = Object.keys(state).reduce((prev, key) => {
      const slice = state[key]
      if (!fixedFilters[parseInt(key, 10)]) {
        return prev + slice.length
      }
      return prev
    }, 0)

    totalSelectedLocal = totalSelectedLocal + selectedBrands.length + selectedCollections.length

    let showButtons = totalSelectedLocal > 0 || totalAppliedFitlers > 0
    const handleSave = () => {
      // Filter out empty arrays
      const filters = {}
      Object.keys(state).forEach((key) => {
        if (state[key].length) {
          filters[key] = state[key]
        }
      })
      dispatch({ type: 'set-filters', filters })
      dispatch({ type: 'set-brands', value: selectedBrands })
      dispatch({ type: 'set-collections', value: selectedCollections })
      onRequestClose()
    }

    const filteredAttributes = getFilteredAttributes(attributes)

    return (
      <>
        <Background
          key="filters-backdrop"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ delay: 0.1, ease: 'easeOut', duration: 0.2 }}
        >
          <Header
            onClickClose={onRequestClose}
            hasFilters={showButtons}
            onClickReset={() => {
              setState({})
              setSelectedBrands([])
              setSelectedCollections([])
              dispatch({ type: 'clear-filters' })
            }}
          >
            Filters
          </Header>
          <Content ref={ref}>
            {priceCategory && (
              <Accordion
                label={t('price', { ns: 'other' })}
                filterCount={
                  state[priceCategory.attributeID] ? state[priceCategory.attributeID].length : 0
                }
              >
                {priceCategory.articleFilterAttributeValues.map((attribute) => {
                  return (
                    <Checkbox
                      mb="1rem"
                      name={`price-${attribute.attributeValueID}`}
                      key={`price-${attribute.attributeValueID}`}
                      checked={
                        state[priceCategory.attributeID]
                          ? state[priceCategory.attributeID].includes(attribute.attributeValueID)
                          : false
                      }
                      onChange={() => toggle(priceCategory.attributeID, attribute.attributeValueID)}
                      label={attribute.attributeValue}
                    ></Checkbox>
                  )
                })}
              </Accordion>
            )}
            {collectionOptions.length > 0 && (
              <Accordion
                label={t(isPreOwned ? 'brands' : 'collection', { ns: 'filters' })}
                filterCount={collections.length}
              >
                {collectionOptions.map((collection) => {
                  return (
                    <Checkbox
                      mb="1rem"
                      name={`collection-${collection.collectionNumber}`}
                      key={`collection-${collection.collectionNumber}`}
                      checked={selectedCollections.includes(collection.collectionNumber)}
                      onChange={() => toggleCollection(collection.collectionNumber)}
                      label={collection.collection}
                    ></Checkbox>
                  )
                })}
              </Accordion>
            )}
            {brandOptions.length > 0 && (
              <Accordion label={t('brands', { ns: 'filters' })} filterCount={brands.length}>
                {showRolexLink && (
                  <LocalizedLink href="/rolex">
                    <Link mb="1rem" variant="brown" display="inline-block">
                      Rolex
                    </Link>
                  </LocalizedLink>
                )}
                {brandOptions.map((brand) => {
                  return (
                    <Checkbox
                      mb="1rem"
                      name={`brand-${brand.merk_nr}`}
                      key={`brand-${brand.merk_nr}`}
                      checked={selectedBrands.includes(brand.merk_nr)}
                      onChange={() => toggleBrand(brand.merk_nr)}
                      label={brand.brand}
                    ></Checkbox>
                  )
                })}
              </Accordion>
            )}
            {filteredAttributes &&
              filteredAttributes.map((attribute) => (
                <Accordion
                  key={attribute.attributeID}
                  label={attribute.attributeName}
                  filterCount={
                    state[attribute.attributeID] ? state[attribute.attributeID].length : 0
                  }
                >
                  {attribute.articleFilterAttributeValues.map((item) => (
                    <Checkbox
                      mb="1rem"
                      name={`${attribute.attributeID}-${item.attributeValueID}`}
                      key={`${attribute.attributeID}-${item.attributeValueID}`}
                      label={item.attributeValue}
                      checked={
                        state[attribute.attributeID]
                          ? state[attribute.attributeID].includes(item.attributeValueID)
                          : false
                      }
                      onChange={() => {
                        toggle(attribute.attributeID, item.attributeValueID)
                      }}
                    />
                  ))}
                </Accordion>
              ))}
          </Content>
        </Background>
        {showButtons && (
          <Footer onClickConfirm={handleSave}>{t('showProducts', { ns: 'other' })}</Footer>
        )}
      </>
    )
  },
)

export default FilterOverlay
